import { CheckCircle2, Circle, ExternalLink, Loader2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDashboardPrerequisites } from '../hooks/use-dashboard-prerequisites';
import { useUIStore } from '../stores/ui.store';

// Define the steps
const setupSteps = [
  {
    id: 'logo',
    label: 'Upload Company Logo',
    isComplete: (status: ReturnType<typeof useDashboardPrerequisites>) => !!status.logoUrl,
    link: '/dashboard/logo',
  },
  {
    id: 'mappings',
    label: 'Configure Field Mappings',
    isComplete: (status: ReturnType<typeof useDashboardPrerequisites>) => status.hasMappings,
    link: '/dashboard/field-mapping',
  },
  {
    id: 'settings',
    label: 'Visit Settings Page',
    isComplete: (status: ReturnType<typeof useDashboardPrerequisites>) => status.hasViewedSettings,
    link: '/dashboard/settings',
  },
  // Add more steps here if needed in the future
];

/**
 * Checklist component to guide users through initial dashboard setup.
 */
export const DashboardChecklist = () => {
  const status = useDashboardPrerequisites();
  const { prerequisitesMet, dashboardInitialized } = status;
  const setShowChecklistModal = useUIStore((state) => state.setShowChecklistModal);

  // Don't show if prerequisites are already met
  if (prerequisitesMet) {
    return null;
  }

  const completedCount = setupSteps.filter((step) => step.isComplete(status)).length;
  const totalSteps = setupSteps.length;
  const progress = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

  return (
    <div className="fixed bottom-4 right-4 z-40 w-72 max-w-[calc(100vw-2rem)]">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="p-3 border-b border-gray-100 bg-gray-50 relative">
          <button
            onClick={() => setShowChecklistModal(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            aria-label="Close checklist"
          >
            <X className="h-4 w-4" />
          </button>
          <h3 className="text-sm font-semibold text-gray-700">Get Started</h3>
          <p className="text-xs text-gray-500 mt-1">
            {dashboardInitialized
              ? `${completedCount} of ${totalSteps} complete`
              : 'Loading setup status...'}
          </p>
          {/* Progress Bar */}
          {dashboardInitialized && (
            <div className="w-full bg-gray-200 rounded-full h-1 mt-2">
              <div
                className="bg-blue-600 h-1 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          )}
          {!dashboardInitialized && (
            <div className="w-full bg-gray-200 rounded-full h-1 mt-2 relative overflow-hidden">
              <div className="absolute inset-0 bg-blue-300 opacity-50"></div>
            </div>
          )}
        </div>

        {/* List of Steps */}
        <ul className="divide-y divide-gray-100 p-2">
          {!dashboardInitialized && (
            <li className="py-2 px-1 flex items-center text-gray-500 text-sm">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Checking setup status...
            </li>
          )}
          {dashboardInitialized &&
            setupSteps.map((step) => {
              const isComplete = step.isComplete(status);
              return (
                <li key={step.id} className="py-1.5 px-1">
                  <Link
                    to={step.link}
                    className={`flex items-center justify-between text-sm rounded-md p-1.5 group ${
                      isComplete
                        ? 'text-gray-400 cursor-default'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={(e) => isComplete && e.preventDefault()} // Prevent navigation if complete
                  >
                    <div className="flex items-center">
                      {isComplete ? (
                        <CheckCircle2 className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" />
                      ) : (
                        <Circle className="h-4 w-4 mr-2 text-gray-300 flex-shrink-0" />
                      )}
                      <span className={isComplete ? 'line-through' : ''}>{step.label}</span>
                    </div>
                    {!isComplete && (
                      <ExternalLink className="h-3.5 w-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </Link>
                </li>
              );
            })}
        </ul>
      </div>
    </div>
  );
};
